import React from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { motion } from 'framer-motion';
import { TrendingUp, Play, ArrowRight } from 'lucide-react';

const stats = [
    { value: '90%', label: 'Placement Rate' },
    { value: '₹4.5L', label: 'Avg Starting Salary' },
    { value: '200+', label: 'Careers Transformed' },
    { value: '4.8/5', label: 'Student Rating' },
];

const stories = [
    {
        name: 'Amit Sharma',
        course: 'UI/UX Design with AI',
        before: 'Fresher with no design experience',
        after: 'UI/UX Designer at Razorpay',
        salary: '₹6.5 LPA',
        quote: 'The AI-first approach gave me an edge no other fresher had.',
        initials: 'AS',
    },
    {
        name: 'Neha Kulkarni',
        course: 'App Development with AI',
        before: 'Commerce graduate',
        after: 'Flutter Developer at a Startup',
        salary: '₹5.2 LPA',
        quote: 'Went from zero coding knowledge to building real apps in 14 weeks.',
        initials: 'NK',
    },
    {
        name: 'Rohan Deshmukh',
        course: 'UI/UX Design with AI',
        before: 'Graphic designer earning ₹20k/month',
        after: 'Senior UX Designer at Flipkart',
        salary: '₹12 LPA',
        quote: 'The portfolio projects helped me negotiate a 3x salary jump.',
        initials: 'RD',
    },
    {
        name: 'Sakshi Patil',
        course: 'App Development with AI',
        before: 'B.Tech CSE student',
        after: 'Full-Stack Developer at TCS',
        salary: '₹7 LPA',
        quote: 'The real client simulation was the most valuable part of the course.',
        initials: 'SP',
    },
    {
        name: 'Karthik Nair',
        course: 'UI/UX Design with AI',
        before: 'Self-taught designer, no portfolio',
        after: 'Freelance designer earning ₹80k/month',
        salary: '₹9.6 LPA equivalent',
        quote: 'Now I have more clients than I can handle. The course changed everything.',
        initials: 'KN',
    },
    {
        name: 'Pooja Verma',
        course: 'App Development with AI',
        before: 'Working professional in non-tech',
        after: 'React Native Developer at Infosys',
        salary: '₹8.5 LPA',
        quote: 'The placement support and mock interviews made all the difference.',
        initials: 'PV',
    },
];

const videoTestimonials = [
    { name: 'Amit S.', role: 'UI/UX Designer at Razorpay', duration: '3:45' },
    { name: 'Neha K.', role: 'Flutter Developer', duration: '2:30' },
    { name: 'Rohan D.', role: 'Senior UX Designer at Flipkart', duration: '4:12' },
];

const companies = ['Razorpay', 'Flipkart', 'TCS', 'Infosys', 'Zerodha', 'Freshworks', 'Zoho', 'Swiggy'];

const SuccessStories = () => {
    return (
        <>
            <Helmet>
                <title>Success Stories | PyrusMedia Academy</title>
                <meta name="description" content="See how our students have transformed their careers. Real stories, real results from PyrusMedia Academy graduates." />
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-20 left-1/3 w-[400px] h-[400px] bg-[#bebd19]/5 rounded-full blur-[120px]" />
                <div className="container mx-auto px-4 relative z-10">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
                    >
                        Our Students Ship Real Projects<br />
                        <span className="text-[#bebd19]">— And Get Hired</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-gray-500 max-w-2xl mx-auto"
                    >
                        Real stories from students who transformed their careers with PyrusMedia Academy.
                    </motion.p>
                </div>
            </section>

            {/* Stats */}
            <Section className="bg-gray-50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    {stats.map((stat, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="text-center"
                        >
                            <div className="text-4xl font-bold text-[#bebd19] mb-2">{stat.value}</div>
                            <div className="text-gray-500">{stat.label}</div>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Success Stories Grid */}
            <Section className="bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Career Transformations</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {stories.map((story, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:border-[#bebd19] transition-all"
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-full bg-[#bebd19]/20 flex items-center justify-center text-xl font-bold text-[#bebd19] shrink-0">
                                    {story.initials}
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900">{story.name}</h3>
                                    <p className="text-sm text-[#bebd19]">{story.course}</p>
                                </div>
                            </div>

                            <div className="space-y-3 mb-6">
                                <div className="flex items-start gap-3">
                                    <span className="text-xs text-gray-400 uppercase w-16 shrink-0 pt-0.5">Before</span>
                                    <span className="text-sm text-gray-500">{story.before}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-xs text-[#bebd19] uppercase font-semibold w-16 shrink-0 pt-0.5">After</span>
                                    <span className="text-sm text-gray-900 font-medium">{story.after}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 mb-4 p-3 bg-[#bebd19]/10 rounded-lg">
                                <TrendingUp size={16} className="text-[#bebd19]" />
                                <span className="text-sm font-bold text-gray-900">{story.salary}</span>
                            </div>

                            <p className="text-gray-500 text-sm italic">"{story.quote}"</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Video Testimonials */}
            <Section className="bg-gray-50">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Video Stories</h2>
                <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                    {videoTestimonials.map((v, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="bg-gray-200 rounded-2xl aspect-video relative overflow-hidden group cursor-pointer"
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-14 h-14 rounded-full bg-[#bebd19] flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Play size={24} className="text-black ml-1" />
                                </div>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-4">
                                <div className="text-white font-bold text-sm">{v.name}</div>
                                <div className="text-white/70 text-xs">{v.role}</div>
                            </div>
                            <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded">
                                {v.duration}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Where Our Students Work */}
            <Section className="bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Where Our Students Work</h2>
                <p className="text-gray-500 text-center mb-12">Graduates have landed roles at leading tech companies.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                    {companies.map((c, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.05 }}
                            className="bg-gray-50 h-16 rounded-xl border border-gray-200 flex items-center justify-center"
                        >
                            <span className="text-gray-400 font-bold">{c}</span>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* CTA */}
            <Section className="bg-[#bebd19]">
                <div className="text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Write Your Success Story</h2>
                    <p className="text-black/70 text-lg mb-8 max-w-xl mx-auto">
                        Join 200+ students who've already transformed their careers. Your story could be next.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button to="/courses/ui-ux-design-with-ai" className="bg-black text-white hover:bg-gray-800">
                            Start with UI/UX Design
                        </Button>
                        <Button to="/courses/app-development-with-ai" variant="outline" className="border-black text-black hover:bg-black/10">
                            Start with App Development
                        </Button>
                    </div>
                </div>
            </Section>
        </>
    );
};

export default SuccessStories;
